import React, { useState, useCallback, useMemo } from 'react';
import { Modal, Box, SpaceBetween, Button, ButtonDropdown, Textarea, Alert, Spinner } from '@amzn/awsui-components-core';
import { AttendeePriority } from '../../interfaces/interface';
import { PASTE_ATTENDEES_PLACEHOLDER } from '../../constants/EAHuddleConstants';
import { logger } from 'src/logger';

interface PasteListModalProps {
  visible: boolean;
  onDismiss: () => void;
  onSubmit: (names: string[], priority: AttendeePriority) => Promise<void>;
}

/**
 * Parses raw input text into individual names/aliases.
 * Supports comma, semicolon, and newline-separated formats.
 */
function parseInput(raw: string): string[] {
  return raw
    .split(/[\n,;]+/)
    .map((s) => s.trim())
    .filter((s) => s.length > 0);
}

export const PasteListModal: React.FC<PasteListModalProps> = ({ visible, onDismiss, onSubmit }) => {
  const [inputText, setInputText] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const parsedNames = useMemo(() => parseInput(inputText), [inputText]);

  const handleClose = useCallback(() => {
    if (isLoading) return;
    setInputText('');
    setError('');
    onDismiss();
  }, [onDismiss, isLoading]);

  const handleSubmit = useCallback(
    async (priority: AttendeePriority) => {
      if (parsedNames.length === 0) {
        setError('Please enter at least one name or alias.');
        return;
      }
      setError('');
      setIsLoading(true);
      try {
        await onSubmit(parsedNames, priority);
        setInputText('');
        onDismiss();
      } catch (err) {
        logger.error('Paste list submission failed', { error: err });
        setError('Failed to resolve attendees. Please try again.');
      } finally {
        setIsLoading(false);
      }
    },
    [parsedNames, onSubmit, onDismiss]
  );

  if (!visible) return null;

  return (
    <Modal
      visible={true}
      onDismiss={handleClose}
      header="Paste Attendees"
      size="medium"
      footer={
        <Box float="right">
          <SpaceBetween direction="horizontal" size="xs">
            <Button variant="link" onClick={handleClose} disabled={isLoading}>
              Cancel
            </Button>
            <span className="blue-button">
              <ButtonDropdown
                variant="primary"
                loading={isLoading}
                disabled={parsedNames.length === 0 || isLoading}
                items={[
                  { id: AttendeePriority.Critical, text: 'Add as Critical' },
                  { id: AttendeePriority.Required, text: 'Add as Required' },
                  { id: AttendeePriority.Optional, text: 'Add as Optional' }
                ]}
                onItemClick={({ detail }) => handleSubmit(detail.id as AttendeePriority)}
              >
                Add Attendees
              </ButtonDropdown>
            </span>
          </SpaceBetween>
        </Box>
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
        {error && (
          <Alert statusIconAriaLabel="Error" type="error">
            {error}
          </Alert>
        )}
        <Textarea
          value={inputText}
          onChange={({ detail }) => {
            setInputText(detail.value);
            setError('');
          }}
          placeholder={PASTE_ATTENDEES_PLACEHOLDER}
          rows={8}
          disabled={isLoading}
        />
        {parsedNames.length > 0 && !isLoading && (
          <Box color="text-body-secondary" fontSize="body-s">
            {parsedNames.length} {parsedNames.length === 1 ? 'attendee' : 'attendees'} detected
          </Box>
        )}
        {isLoading && (
          <Box color="text-body-secondary" fontSize="body-s">
            <SpaceBetween direction="horizontal" size="xs" alignItems="center">
              <Spinner size="normal" />
              <span>
                Resolving {parsedNames.length} {parsedNames.length === 1 ? 'attendee' : 'attendees'}...
              </span>
            </SpaceBetween>
          </Box>
        )}
      </div>
    </Modal>
  );
};
